import {
  useFetchCheckListByProject,
} from "hooks/api/checklistApiHooks";
import { useFetchAllTemplateChecklists } from "hooks/api/templateChecklistApiHooks";
import useDialog from "hooks/dialog/useDialog";
import { useManagementTextField } from "hooks/forms/ManagementHooks";
import { useAutocomplete } from "hooks/forms/FormHooks";
import { useState } from "react";
import {
  createChecklist,
  deleteChecklist,
  duplicateChecklist,
} from "services/api/checklist/checklistApi";
import {
  createChecklistItem,
  deleteChecklistItem,
  updateChecklistItem,
} from "services/api/checklistItems/checklistItemsApi";
import { useEditProjectContext } from "../context/EditProjectContext";
import { useQueryClient } from "react-query";

export const useProjectChecklist = ({
  projectId,
  setDisabled,
  initializeLoadingToast,
  updateLoadingToast,
}) => {
  const { checklistLoading, checklistData } = useFetchCheckListByProject(projectId);

  // const { projectData } = useEditProjectContext(); // Removido temporariamente - não utilizado

  const {
    allTemplatesLoading: templatesLoading,
    allTemplatesData,
  } = useFetchAllTemplateChecklists();

  const templatesData = allTemplatesData?.map((template) => {
    return { id: template.id, name: template.title };
  });



  // const handleUpdateTemplateCheckBox = () => {
  //   const isChecked = !checkbox.checked;

  //   checkbox.onCustomChange(isChecked);


    

  // }

  const { accordionsState, updateAccordionState } = useEditProjectContext();
  const handleAccordionChange = (e, isExpanded) => {
    updateAccordionState("checklistOpen", isExpanded);
  };
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);


  const { reset: resetItemName, ...itemName } = useManagementTextField("");

  const { reset: resetTemplate, ...template } = useAutocomplete("");

  const queryClient = useQueryClient();



  const handleDeleteChecklist = async (checklistId) => {
    let deleteToastId;
    try {
      setDisabled(true);
      setDeleteLoading(true);

      deleteToastId = initializeLoadingToast({
        text: `Excluindo Checklist...`,
      });

      if (!checklistData) return;

      const { status } = await deleteChecklist(checklistId);

      if (status === 200) {
        updateLoadingToast({
          id: deleteToastId,
          text: `Checklist Excluído com sucesso`,
          type: "success",
        });
      }

      queryClient.setQueryData(["checklistByProject", projectId], null);
    } catch (error) {
      updateLoadingToast({
        id: deleteToastId,
        text: error.response.data.message ?? "Erro ao excluir Checklist, tente novamente",
        type: "error",
      });
    } finally {
      setDisabled(false);
      setDeleteLoading(false);
      handleDeleteDialogClose();
    }
  };

  const handleCreateChecklist = async () => {
    let createToastId;
    try {
      setCreateLoading(true);
      setDisabled(true);
      createToastId = initializeLoadingToast({
        text: `Criando Checklist...`,
      });

      const { status } = await createChecklist({
        projectId,
        title: "Checklist do Projeto",
        isTemplate: false,
      });

      if (status === 200) {
        updateLoadingToast({
          id: createToastId,
          text: `Checklist Criado com sucesso`,
          type: "success",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["checklistByProject", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projectActivityFeed", projectId] });
    } catch (error) {
      updateLoadingToast({
        id: createToastId,
        text: error.response.data.message ?? "Erro ao criar Checklist, tente novamente",
        type: "error",
      });
    } finally {
      setCreateLoading(false);
      setDisabled(false);
      handleCreateDialogClose();
    }
  };

  const handleDuplicateCheckList = async () => {
    let createToastId;
    try {
      if (!template.value) return;

      setCreateLoading(true);
      setDisabled(true);
      createToastId = initializeLoadingToast({
        text: `Duplicando Checklist...`,
      });

      const { status } = await duplicateChecklist({
        projectId,
        checklistId: template.value.id,
      });

      if (status === 200) {
        updateLoadingToast({
          id: createToastId,
          text: `Checklist Criado com sucesso`,
          type: "success",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["checklistByProject", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projectActivityFeed", projectId] });
    } catch (error) {
      updateLoadingToast({
        id: createToastId,
        text: error.response.data.message ?? "Erro ao duplicar Checklist, tente novamente",
        type: "error",
      });
    } finally {
      setCreateLoading(false);
      setDisabled(false);
      handleDuplicateDialogClose();
    }
  };

  const handleCreateItem = async () => {
    // Validação: campo não pode estar vazio
    if (!itemName.value || itemName.value.trim() === "") {
      return; // Não prossegue se campo estiver vazio
    }

    let createToastId;
    try {
      setCreateLoading(true);
      setDisabled(true);
      createToastId = initializeLoadingToast({
        text: `Criando Item...`,
      });

      if (!checklistData) return;

      const { status, data } = await createChecklistItem({
        checklistId: checklistData.id,
        content: itemName.value.trim(), // Remove espaços em branco
        isChecked: false,
        order: checklistData.checklistItems.length + 1,
      });

      if (status === 200) {
        updateLoadingToast({
          id: createToastId,
          text: `Item Criado com sucesso`,
          type: "success",
        });
        
        // Limpa o campo após sucesso
        resetItemName();
      }

      // Atualiza o estado local do checklist
      checklistData.checklistItems.push(data);
      
      // Atualiza o cache do React Query para refletir a mudança
      queryClient.setQueryData(["checklistByProject", projectId], {
        ...checklistData,
        checklistItems: [...checklistData.checklistItems]
      });
    } catch (error) {
      updateLoadingToast({
        id: createToastId,
        text: error.response.data.message ?? "Erro ao criar Item, tente novamente",
        type: "error",
      });
    } finally {
      setCreateLoading(false);
      setDisabled(false);
      handleCreateDialogItemClose();
    }
  };

  // Função para lidar com o submit do formulário (Enter)
  const handleFormSubmit = async (event) => {
    event.preventDefault(); // Previne o comportamento padrão do form
    await handleCreateItem(); // Chama a função de criação
  };

  const handleDeleteItem = async (itemId) => {
    let deleteToastId;
    try {
      setDisabled(true);
      deleteToastId = initializeLoadingToast({
        text: `Excluindo Item...`,
      });

      if (!checklistData) return;

      const { status } = await deleteChecklistItem(itemId);

      if (status === 200) {
        updateLoadingToast({
          id: deleteToastId,
          text: `Item Excluído com sucesso`,
          type: "success",
        });
      }

      checklistData.checklistItems = checklistData.checklistItems.filter(
        (item) => item.id !== itemId
      );
    } catch (error) {
      updateLoadingToast({
        id: deleteToastId,
        text: error.response.data.message ?? "Erro ao excluir Item, tente novamente",
        type: "error",
      });
    } finally {
      setDisabled(false);
    }
  };



  const handleSaveChecklistItem = async (item) => {
    try {
      const { status } = await updateChecklistItem({
        id: item.id,
        content: itemName.value,
        isChecked: item.isChecked,
        order: item.order,
      });

      if (status === 200) {
        // updateLoadingToast({
        //   id: createToastId,
        //   text: `Checklist Atualizado com sucesso`,
        //   type: "success",
        // });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const {
    open: createDialogOpen,
    handleClickOpen: handleCreateDialogOpen,
    handleClickClose: handleCreateDialogClose,
  } = useDialog();

  const {
    open: createItemDialogOpen,
    handleClickOpen: handleCreateItemDialogOpen,
    handleClickClose: handleCreateDialogItemClose,
  } = useDialog();

  const {
    open: deleteDialogOpen,
    handleClickOpen: handleDeleteDialogOpen,
    handleClickClose: handleDeleteDialogClose,
  } = useDialog();

  const {
    open: duplicateDialogOpen,
    handleClickOpen: handleDuplicateDialogOpen,
    handleClickClose: handleDuplicateDialogClose,
  } = useDialog();

  return {
    accordionsState,
    handleAccordionChange,
    checklistLoading,
    checklistData,
    createLoading,
    handleCreateChecklist,
    handleCreateItem,
    handleFormSubmit,
    createDialogOpen,
    handleCreateDialogOpen,
    handleCreateDialogClose,
    createItemDialogOpen,
    handleCreateItemDialogOpen,
    handleCreateDialogItemClose,

    deleteDialogOpen,
    handleDeleteDialogOpen,
    handleDeleteDialogClose,
    deleteLoading,

    duplicateDialogOpen,
    handleDuplicateDialogOpen,
    handleDuplicateDialogClose,

    template,
    templatesLoading,
    templatesData: templatesData || [],
    itemName,
    handleDeleteItem,
    handleDeleteChecklist,
    handleDuplicateCheckList,
    handleSaveChecklistItem,
  };
};
